import React, { useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaUsers, FaTimes } from "react-icons/fa";
import PostCard from "../profile/PostCard";
import MarketplaceListingsSection from "../marketplace/MarketplaceListingsSection";
import { filterPostsForMainFeed, filterExpiredBattles } from "../../utils/feedPostTypeFilters";
import styles from "../../styles/pages/feed/FeedPage.module.css";

const FeedPostsTab = ({
  active,
  posts,
  activeFilter,
  activeTab,
  feedError,
  loading,
  loadingMore,
  hasMore,
  page,
  isRefreshing,
  loadMore,
  fetchFeed,
  newPostsCount,
  onNewPostsRefresh,
  isFirstTimeUser,
  welcomeDismissed,
  onDismissWelcome,
  suggestedUsers,
  trendingCategories,
  onFollowUser,
  onOpenPost,
  onPostDeleted,
  onLikeUpdate,
  onSaveUpdate,
  onCommentUpdate,
  onVoteUpdate,
  onPostUpdated,
  onEditPost,
  onCommentOpen,
  activeDecision = null,
  shopperCategoryKeys = null,
  scrollRoot = null,
}) => {
  const loadMoreRef = useRef(null);

  const feedItems = useMemo(() => {
    const list = Array.isArray(posts) ? posts : [];
    const showStandardOnly =
      activeTab === null && activeFilter === "following";
    let basePosts = showStandardOnly ? filterPostsForMainFeed(list) : list;
    if (!showStandardOnly && activeFilter === "forYou") {
      basePosts = filterExpiredBattles(basePosts);
    }
    if (!basePosts.length) return [];

    const items = [];
    basePosts.forEach((post, index) => {
      items.push({ type: "post", key: `post-${post._id}`, post });
      // Insert a single discover rail after the 5th post only. Inserting
      // one after every 5th post caused a fresh `/listings` fetch (and a
      // duplicated "Discover more" block) for every page of infinite
      // scroll, fanning out repeated identical marketplace requests.
      if (index === 4) {
        items.push({ type: "discover", key: `discover-${post._id}-${index}` });
      }
    });
    return items;
  }, [posts, activeFilter, activeTab]);

  useEffect(() => {
    if (!active || !loadMoreRef.current || !hasMore || loading) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      {
        root: scrollRoot || null,
        rootMargin: "200px",
      },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [active, hasMore, loadingMore, loading, isRefreshing, page, loadMore, scrollRoot]);

  if (!active) return null;

  return (
    <>
      {newPostsCount > 0 && (
        <button
          type="button"
          className={styles.newPostsBanner}
          onClick={onNewPostsRefresh}
          aria-live="polite"
        >
          ↑ {newPostsCount} new post{newPostsCount === 1 ? "" : "s"} - click to
          refresh
        </button>
      )}

      {isFirstTimeUser && !welcomeDismissed && (
        <div className={styles.welcomeBanner}>
          <button
            type="button"
            className={styles.welcomeDismiss}
            onClick={onDismissWelcome}
            aria-label="Dismiss welcome banner"
          >
            <FaTimes />
          </button>
          <h3>Welcome Home!</h3>
          <p>Follow people and explore categories to see posts here.</p>
          {suggestedUsers.length > 0 && (
            <div className={styles.welcomeSuggested}>
              {suggestedUsers.slice(0, 3).map((user) => (
                <button
                  key={user._id}
                  type="button"
                  className={styles.welcomeFollowPill}
                  onClick={() => onFollowUser(user._id)}
                >
                  + Follow @{user.username}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div className={styles.skeletonList} aria-hidden="true">
          {[0, 1, 2].map((idx) => (
            <div key={idx} className={styles.skeletonCard}>
              <div className={styles.skeletonHeader}>
                <span className={styles.skeletonAvatar} />
                <div className={styles.skeletonHeaderText}>
                  <span className={styles.skeletonLineShort} />
                  <span className={styles.skeletonLineTiny} />
                </div>
              </div>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonContent}>
                <span className={styles.skeletonLineMedium} />
                <span className={styles.skeletonLineFull} />
                <span className={styles.skeletonLineMedium} />
              </div>
              <div className={styles.skeletonActions}>
                <span className={styles.skeletonAction} />
                <span className={styles.skeletonAction} />
                <span className={styles.skeletonAction} />
                <span className={styles.skeletonAction} />
              </div>
            </div>
          ))}
        </div>
      ) : feedError && posts.length === 0 ? (
        <div className={styles.emptyState} role="alert">
          <FaUsers className={styles.emptyIcon} />
          <h2>Could not load feed</h2>
          <p>{feedError}</p>
          <div className={styles.emptyActions}>
            <button
              type="button"
              className={styles.exploreButton}
              onClick={() => fetchFeed(1)}
            >
              Try again
            </button>
          </div>
        </div>
      ) : feedItems.length > 0 ? (
        <>
          <div className={styles.postsContainer}>
            {feedItems.map((item) => {
              if (item.type === "discover") {
                return (
                  <div key={item.key} className={styles.discoveryCard}>
                    <h3>Discover more</h3>
                    {suggestedUsers.length > 0 && (
                      <div className={styles.discoveryUsers}>
                        {suggestedUsers.slice(0, 3).map((user) => (
                          <button
                            key={user._id}
                            type="button"
                            className={styles.welcomeFollowPill}
                            onClick={() => onFollowUser(user._id)}
                          >
                            + Follow @{user.username}
                          </button>
                        ))}
                      </div>
                    )}
                    {trendingCategories.length > 0 && (
                      <div className={styles.discoveryTags}>
                        {trendingCategories.slice(0, 3).map((category) => (
                          <Link
                            key={category._id}
                            to={`/category/${category._id}`}
                            className={styles.tagItem}
                          >
                            {category.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    <MarketplaceListingsSection
                      title="Deals on Marketplace"
                      queryParams={{ sort: "recommended" }}
                      limit={2}
                      compact
                      widgetSource="feed_discover_rail"
                      className={styles.feedMarketplaceRail}
                    />
                  </div>
                );
              }

              const post = item.post;
              return (
                <div
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  className={styles.postClickableWrapper}
                  onClick={(event) => onOpenPost(post, event)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    const tag = e.target?.tagName;
                    if (
                      tag === "INPUT" ||
                      tag === "TEXTAREA" ||
                      tag === "SELECT" ||
                      e.target?.isContentEditable
                    ) {
                      return;
                    }
                    e.preventDefault();
                    onOpenPost(post, e);
                  }}
                >
                  <PostCard
                    post={post}
                    activeDecision={activeDecision}
                    shopperCategoryKeys={shopperCategoryKeys}
                    onDelete={onPostDeleted}
                    onLikeUpdate={onLikeUpdate}
                    onSaveUpdate={onSaveUpdate}
                    onCommentUpdate={onCommentUpdate}
                    onVoteUpdate={onVoteUpdate}
                    onPostUpdated={onPostUpdated}
                    onEdit={() => onEditPost(post)}
                    commentAction="modal"
                    onCommentNavigate={() => onCommentOpen(post)}
                    disableCardClick={true}
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.loadMoreContainer}>
            <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
              {loadingMore && (
                <div className={styles.loadMoreBusy}>
                  <span className={styles.loadMoreSpinner} />
                  <span className={styles.loadMoreBusyText}>
                    Loading more posts...
                  </span>
                </div>
              )}
              {!loadingMore && hasMore && (
                <span className={styles.loadMoreIdle}>Scroll to load more</span>
              )}
              {!loadingMore && !hasMore && (
                <div className={styles.endOfFeed}>
                  <span className={styles.endOfFeedCheck} aria-hidden="true">
                    ✓
                  </span>
                  <div className={styles.endOfFeedText}>
                    You&apos;ve reached the end. Explore more categories?
                  </div>
                  <div className={styles.endOfFeedLinks}>
                    <Link to="/" className={styles.endOfFeedLink}>
                      Home
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>
          <FaUsers className={styles.emptyIcon} />
          <h2>
            {activeFilter === "following"
              ? "No posts from people you follow"
              : "Your feed is empty"}
          </h2>
          <p>
            {activeFilter === "following"
              ? "Follow more people to see their posts here."
              : "Follow people and explore categories to fill your feed with product posts."}
          </p>
          <div className={styles.emptyActions}>
            <Link to="/users/search" className={styles.findUsersButton}>
              Connect with People
            </Link>
            <Link to="/" className={styles.exploreButton}>
              Home
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(FeedPostsTab);
